import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles, featuredArticles, faqs } from '@/mocks/articles';

export default function Home() {
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '同人创作指南',
    url: 'https://doujin-guide.com',
    description: '专注同人漫画创作技巧、发布平台与版权知识的综合性指南网站，助力每一位二次元爱好者的创作之路。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://doujin-guide.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '同人创作指南',
    url: 'https://doujin-guide.com',
    description: '专注同人漫画创作技巧、发布平台与版权知识的综合性指南网站。',
    sameAs: ['https://weibo.com/doujin-guide', 'https://space.bilibili.com/doujin-guide'],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const featuredArticlesData = featuredArticles
    .map((id) => articles.find((a) => a.id === id))
    .filter(Boolean);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Japanese%20manga%20artist%20creative%20studio%20workspace%20with%20sketchbooks%20drawing%20tablet%20and%20anime%20art%20on%20walls%2C%20warm%20golden%20lighting%2C%20artistic%20atmosphere%2C%20cozy%20creative%20environment%2C%20soft%20bokeh%20background&width=1600&height=900&seq=hero-doujin-01&orientation=landscape"
              alt="同人漫画创作指南"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
            <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
              <div className="w-full text-center max-w-3xl mx-auto">
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4 leading-tight">
                  同人漫画创作指南与资源大全
                </h1>
                <p className="text-sm md:text-lg text-background-50/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                  从脚本写作、分镜设计到绘制技法和发布平台，全方位覆盖同人漫画创作的每一个环节，帮你找到最适合自己的创作路径。
                </p>
                <Link
                  to="/category/jiaoben-xiezuo"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  开始学习
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </section>

          {/* Categories Section */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                探索创作分类
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                五大核心分类，覆盖同人漫画创作从灵感到发布的全流程
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categories.map((cat, index) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex items-start gap-4 p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-accent-100 flex-shrink-0">
                    <i className={`${cat.icon} text-xl text-accent-500`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors mb-1">
                      {cat.name}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                      {cat.description}
                    </p>
                    <span className="inline-block mt-2 text-xs text-foreground-500">
                      {cat.count} 篇文章
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Featured Articles */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                    精选文章
                  </h2>
                  <p className="text-sm text-foreground-600">
                    编辑精选的高质量同人漫画创作指南
                  </p>
                </div>
                <Link
                  to="/category/jiaoben-xiezuo"
                  className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {featuredArticlesData.map(
                  (article) =>
                    article && (
                      <ArticleCard
                        key={article.id}
                        title={article.title}
                        excerpt={article.excerpt}
                        image={article.image}
                        category={article.category}
                        categorySlug={article.categorySlug}
                        date={article.date}
                        readTime={article.readTime}
                        slug={article.slug}
                      />
                    )
                )}
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  最新文章
                </h2>
                <p className="text-sm text-foreground-600">
                  持续更新的同人漫画创作内容
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.slice(0, 6).map((article) => (
                <ArticleCard
                  key={article.id}
                  title={article.title}
                  excerpt={article.excerpt}
                  image={article.image}
                  category={article.category}
                  categorySlug={article.categorySlug}
                  date={article.date}
                  readTime={article.readTime}
                  slug={article.slug}
                />
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-600">
                  关于同人漫画创作，你可能想了解的问题
                </p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <h3 className="text-sm md:text-base font-medium text-foreground-950 pr-4">
                        {faq.question}
                      </h3>
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0" />
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative w-full py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Manga%20drawing%20tablet%20with%20colorful%20anime%20illustration%20in%20progress%2C%20creative%20studio%20workspace%2C%20warm%20ambient%20lighting%2C%20artistic%20atmosphere%2C%20inspiration%20concept&width=1600&height=600&seq=cta-doujin-02&orientation=landscape"
              alt="开始创作"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50" />
            <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
                准备好开始你的同人漫画创作之旅了吗？
              </h2>
              <p className="text-sm md:text-base text-background-50/80 mb-8">
                从今天开始，用画笔讲述属于你的故事。无论新手还是老手，这里都有你需要的资源。
              </p>
              <Link
                to="/category/jiaoben-xiezuo"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-full font-medium text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                立即探索
                <i className="ri-rocket-line" />
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}